import React, { useEffect, useState } from "react";
import { fetchMaster } from "../../../utils/api";
import {
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import moment from "moment";
import styleClass from "./SmeUsage.module.scss";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";

const SmeUsage = () => {
  const { snackbarError } = useSnackbarMAS();
  const [smeAnnuallyList, setSmeAnnuallyList] = useState([]);
  const [smeMonthlyList, setSmeMonthlyList] = useState([]);
  const [
    smeAnnuallyListLastUpdateDatetime,
    setSmeAnnuallyListLastUpdateDatetime,
  ] = useState();
  const [
    smeMonthlyListLastUpdateDatetime,
    setSmeMonthlyListLastUpdateDatetime,
  ] = useState();
  const [tabValue, setTabValue] = React.useState("annually");
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "Merchant ID",
    direction: "asc",
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const initialFetch = async () => {
    const smeAnnuallyPromise = fetchMaster(
      "/data-lake-report/get-latest-report",
      {
        reportType: "sme-annual",
        percentageLimit: 80,
        page: 0,
        size: 100,
      },
    );

    const smeMonthlyPromise = fetchMaster(
      "/data-lake-report/sme-monthly-list",
      {
        percentageLimit: 80,
        cardType: "VISA",
        page: 0,
        size: 100,
      },
    );

    Promise.all([smeAnnuallyPromise, smeMonthlyPromise])
      .then(([smeAnnuallyResponse, smeMonthlyResponse]) => {
        setIsLoading(false);
        if (smeAnnuallyResponse.data.message === "SUCCESS") {
          setSmeAnnuallyList(smeAnnuallyResponse.data.data);
          setSmeAnnuallyListLastUpdateDatetime(
            smeAnnuallyResponse.data.lastModifiedDateTime,
          );
        }
        if (smeMonthlyResponse.data.message === "SUCCESS") {
          setSmeMonthlyList(smeMonthlyResponse.data.data);
          setSmeMonthlyListLastUpdateDatetime(
            smeMonthlyResponse.data.lastModifiedDateTime,
          );
        }
        if (
          smeAnnuallyResponse.message === "Unauthorized" ||
          smeMonthlyResponse.message === "Unauthorized"
        ) {
          navigate("/user/login");
          return;
        }

        if (smeMonthlyResponse.data.responseCode !== "0000") {
          snackbarError({
            message: `Please contact technical support and try again later (${smeMonthlyResponse.data.responseCode})`,
            durationInMs: 3000,
          });
        }

        if (smeAnnuallyResponse.data.responseCode !== "0000") {
          snackbarError({
            message: `Please contact technical support and try again later (${smeAnnuallyResponse.data.responseCode})`,
            durationInMs: 3000,
          });
        }
      })
      .catch((error) => {
        // Handle errors here
        setIsLoading(false);
        console.error("Error:", error);
      });
    return;
  };

  const csvDownload = async (period) => {
    let response = null;
    let fileName = "";
    switch (period) {
      case "annually":
        response = await fetchMaster("/data-lake-report/get-latest-report", {
          reportType: "sme-annual",
          limit: 1560000 * 0.8,
          isExport: true,
        });
        fileName = `sme-annually-${Date.now()}.csv`;
        break;
      case "monthly":
        response = await fetchMaster("/data-lake-report/sme-monthly-list", {
          limit: 390000 * 0.8,
          cardType: "VISA",
          isExport: true,
        });
        fileName = `sme-monthly-${Date.now()}.csv`;
        break;
      default:
        console.error("Invalid SME report type");
        snackbarError({
          message: "Invalid report",
        });
        return false;
    }

    if (response?.data?.responseCode === "0000") {
      const link = document.createElement("a");
      link.href = response?.data?.data;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } else {
      console.error("Error downloading CSV file:", response?.data?.message);
      snackbarError({
        message: `Please contact technical support and try again later (${response?.data?.responseCode})`,
        durationInMs: 3000,
      });
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const TableRowsLoader = ({ rowsNum, columns }) => {
    return [...Array(rowsNum)].map((row, index) => (
      <TableRow key={index}>
        {Object.keys(columns, index).map(() => {
          return (
            <TableCell component="th" scope="row" key={index}>
              <Skeleton animation="wave" variant="text" />
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  const sortedData = (data, sortConfig) => {
    return data.sort((a, b) => {
      const valA = Number(a[sortConfig.key]) || 0;
      const valB = Number(b[sortConfig.key]) || 0;

      const sortOrder = valA - valB;

      if (sortConfig.direction === "desc") {
        return sortOrder * -1;
      }

      return sortOrder;
    });
  };

  const handleSort = (key, type) => {
    const newSortConfig = {
      key: key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    };

    setSortConfig({
      ...sortConfig,
      key: newSortConfig.key,
      direction: newSortConfig.direction,
    });
    if (type === "annually") {
      setSmeAnnuallyList(sortedData(smeAnnuallyList, newSortConfig));
    }

    if (type === "monthly") {
      setSmeMonthlyList(sortedData(smeMonthlyList, newSortConfig));
    }
  };

  const scrollableTable = (columns, type, data) => {
    return (
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.dataIndex}
                  sortDirection={
                    sortConfig.key === column.dataIndex
                      ? sortConfig.direction
                      : false
                  }
                  style={{ minWidth: column.width }}
                >
                  {column.sorter ? (
                    <TableSortLabel
                      active={sortConfig.key === column.dataIndex}
                      direction={
                        sortConfig.key === column.dataIndex
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort(column.dataIndex, type)}
                      hideSortIcon={false}
                    >
                      {column.title}
                    </TableSortLabel>
                  ) : (
                    column.title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRowsLoader rowsNum={10} columns={columns} />
            ) : (
              data.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.dataIndex}>
                      {column.render ? (
                        column.render(row[column.dataIndex])
                      ) : (
                        <div
                          className={
                            column.dataIndex === "Current Gateway" &&
                            row["Current Gateway"] === row["Gateway"]
                              ? styleClass.sameGateway
                              : ""
                          }
                        >
                          {row[column.dataIndex]}
                        </div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderRemark = () => {
    return (
      <div>
        <div style={{ margin: "8px" }}>
          <ol>
            <li>
              <span className={styleClass.sameGateway}>Red</span> color
              indicates the &quot;current gateway&quot; has reached 80% of its
              limit and hasn&apos;t switched to another processor yet.
            </li>
            <li>
              If a column is marked with &quot;*&quot;, it indicates that the
              column is sortable.
            </li>
            <li>
              Merchants with shared processor&apos;s MID may have duplicate
              records.
            </li>
            <li>Only merchants with SME business plans will be shown.</li>
            <li>
              AMEX referrer mode transactions are excluded from the calculation
              for SME merchants.
            </li>
          </ol>
        </div>
      </div>
    );
  };

  const addCommaToMoneyAmount = (number) => {
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const annuallyTableColumns = [
    {
      title: "*Merchant ID",
      dataIndex: "Merchant ID",
      sorter: true,
      width: 100,
    },
    {
      title: "DBA name",
      dataIndex: "DBA Name",
      width: 120,
    },
    {
      title: "Card Type",
      dataIndex: "Card Type",
      width: 100,
    },
    {
      title: "Current Gateway",
      dataIndex: "Current Gateway",
      width: 160,
    },
    {
      title: "Payment Gateway",
      dataIndex: "Gateway",
      width: 160,
    },
    {
      title: "*Total Amount of Last 12 Months Transaction",
      sorter: true,
      dataIndex: "Total Amount",
      width: 250,
      render: (val) => addCommaToMoneyAmount(Number(val).toFixed(2)),
    },
    {
      title: "*% of Last 12 Months Limit (To Merchant)",
      sorter: true,
      dataIndex: "Annual Limit Percentage To Merchant",
      width: 250,
      render: (val) => Number(val).toFixed(2) + "%",
    },
    {
      title: "*% of Last 12 Months Limit (Actual)",
      sorter: true,
      dataIndex: "Annual Limit Percentage Actual",
      width: 250,
      render: (val) => Number(val).toFixed(2) + "%",
    },
    // {
    //   title: "MCC",
    //   dataIndex: "mcc",
    //   width: 120,
    // },
    {
      title: "From",
      dataIndex: "From",
      width: 120,
    },
    {
      title: "To",
      dataIndex: "To",
      width: 120,
    },
    {
      title: "Store Address",
      dataIndex: "Store Address",
      width: 250,
    },
    {
      title: "Currency",
      dataIndex: "Currency",
      width: 100,
    },
    {
      title: "Processor MID",
      dataIndex: "Gateway Mid",
      width: 200,
    },
    {
      title: "Sales Name",
      dataIndex: "Sales Name",
      width: 200,
    },
    {
      title: "Cost",
      sorter: true,
      dataIndex: "Cost",
      width: 120,
    },
    {
      title: "MDR",
      sorter: true,
      dataIndex: "MDR",
      width: 120,
    },
    {
      title: "Referrer Name",
      dataIndex: "Referrer Name",
      width: 150,
    },
    {
      title: "Referrer Commission",
      sorter: true,
      dataIndex: "Referrer Commission",
      width: 200,
    },
  ];

  const monthlyTableColumns = [
    {
      title: "*Merchant ID",
      dataIndex: "Merchant ID",
      sorter: true,
      width: 100,
    },
    {
      title: "DBA name",
      dataIndex: "DBA Name",
      width: 120,
    },
    {
      title: "Card Type",
      dataIndex: "Card Type",
      width: 100,
    },
    {
      title: "Current Gateway",
      dataIndex: "Current Gateway",
      width: 160,
    },
    {
      title: "Payment Gateway",
      dataIndex: "Gateway",
      width: 160,
    },
    {
      title: "*Total Amount of Last 12 Months Transaction",
      sorter: true,
      dataIndex: "Total Amount",
      width: 250,
      render: (val) => addCommaToMoneyAmount(Number(val).toFixed(2)),
    },
    {
      title: "*% of Last 12 Months Limit (To Merchant)",
      sorter: true,
      dataIndex: "Monthly Limit Percentage To Merchant",
      width: 250,
      render: (val) => Number(val).toFixed(2) + "%",
    },
    {
      title: "*% of Last 12 Months Limit (Actual)",
      sorter: true,
      dataIndex: "Monthly Limit Percentage Actual",
      width: 250,
      render: (val) => Number(val).toFixed(2) + "%",
    },
    // {
    //   title: "MCC",
    //   dataIndex: "mcc",
    //   width: 120,
    // },
    {
      title: "From",
      dataIndex: "From",
      width: 120,
    },
    {
      title: "To",
      dataIndex: "To",
      width: 120,
    },
    {
      title: "Store Address",
      dataIndex: "Store Address",
      width: 250,
    },
    {
      title: "Currency",
      dataIndex: "Currency",
      width: 100,
    },
    {
      title: "Processor MID",
      dataIndex: "Gateway Mid",
      width: 200,
    },
    {
      title: "Sales Name",
      dataIndex: "Sales Name",
      width: 200,
    },
    {
      title: "Cost",
      sorter: true,
      dataIndex: "Cost",
      width: 120,
    },
    {
      title: "MDR",
      sorter: true,
      dataIndex: "MDR",
      width: 120,
    },
    {
      title: "Referrer Name",
      dataIndex: "Referrer Name",
      width: 150,
    },
    {
      title: "Referrer Commission",
      sorter: true,
      dataIndex: "Referrer Commission",
      width: 200,
    },
  ];

  return (
    <div>
      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              aria-label="lab API tabs example"
            >
              <Tab label="Annually" value="annually" />
              <Tab label="Monthly" value="monthly" />
              {/* <Tab label="Item Three" value="3" /> */}
            </TabList>
          </Box>
          <TabPanel value="annually">
            {renderRemark()}
            <Divider />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "16px 0",
              }}
            >
              <div>
                {smeAnnuallyList ? (
                  <Button
                    variant="contained"
                    onClick={() => csvDownload("annually")}
                  >
                    Export
                  </Button>
                ) : null}
              </div>
              <div style={{ margin: "auto 0" }}>
                Last update:{" "}
                {moment(smeAnnuallyListLastUpdateDatetime).format(
                  "YYYY-MM-DD HH:mm:ss",
                )}
              </div>
            </div>
            {scrollableTable(annuallyTableColumns, "annually", smeAnnuallyList)}
          </TabPanel>
          <TabPanel value="monthly">
            {renderRemark()}
            <Divider />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "16px 0",
              }}
            >
              <div>
                {smeMonthlyList ? (
                  <Button
                    variant="contained"
                    onClick={() => csvDownload("monthly")}
                  >
                    Export
                  </Button>
                ) : null}
              </div>
              <div style={{ margin: "auto 0" }}>
                Last update:{" "}
                {moment(smeMonthlyListLastUpdateDatetime).format(
                  "YYYY-MM-DD HH:mm:ss",
                )}
              </div>
            </div>
            {scrollableTable(monthlyTableColumns, "monthly", smeMonthlyList)}
          </TabPanel>
          {/* <TabPanel value="3">Item Three</TabPanel> */}
        </TabContext>
      </Box>
    </div>
  );
};

export default SmeUsage;
